import React, { useState, useEffect, useRef } from 'react';
import { useMemo } from 'react';
import axios from "axios";
import { useParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { v4 as uuidv4 } from 'uuid';
import showdown from 'showdown';

export default function AssessmentsEdit() {
  const params = useParams();
  const assistantId = params.assistantId || params.id;
  const baseApiUri = process.env.REACT_APP_API_URL;

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Chat state ---
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [requestSortKey, setRequestSortKey] = useState(0);
  const chatBoxRef = useRef(null);
const converter = useMemo(() => new showdown.Converter(), []);
  const chatId = assistantId || uuidv4();
  const [nextSortKey, setNextSortKey] = useState(0);

  const API_KEY = '09848734-8745-afrt-8745-8745873487'; // fixed key for now

  // Utility delay
  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // --- Chat Send Logic ---
  const handleSendMessage = async () => {
    if (userInput.trim() === '') return;

    console.log(chatHistory);

    console.log('Next Sort Key', nextSortKey);
    //return;

    const newMessage = { sender: 'user', text: userInput };
    setMessages((prev) => [...prev, newMessage]);
    setUserInput('');

    try {
      // Step 1: Send user query
      const payload = {
        key: API_KEY,
        id: chatId,
        type: 'needs-assessment',
        sortKey: parseInt(nextSortKey),
        query: userInput,
        stream: true
      };

      console.log('Payload', payload);
      console.log('Request Sort Key', requestSortKey);

      const response = await axios.post(`${baseApiUri}/ai/completion`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      const data = response.data;
      console.log('POST /ai/completion →', data);

      const { id, sortKey } = data;
      pollForAssistantResponse(id, sortKey);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { sender: 'assistant', text: 'Something went wrong. Please try again.' };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  // --- Polling for assistant response ---
  const pollForAssistantResponse = async (id, sortKey) => {
    console.log('Polling for assistant response...');
    let attempt = 0;
    console.log(attempt);

    const poll = async () => {
      try {
        let hasMore = true;

        // Add "Analyzing..." placeholder message if not already present
        setMessages((prev) => {
          const hasAnalyzing = prev.some((m) => m.text === 'Analyzing the data...');
          if (!hasAnalyzing) {
            return [...prev, { sender: 'assistant', text: 'Analyzing the data...' }];
          }
          return prev;
        });

        while (hasMore) {
          attempt++;
          const endpoint = `${baseApiUri}/ai/completion/${id}/${sortKey}`;
          const response = await axios.get(endpoint);
          const completion = response.data?.response;
          const status = completion?.status;
          const fragment = completion?.message || '';

          if (fragment) {
            const html = converter.makeHtml(fragment);
            setMessages((prev) => {
              const updated = [...prev];
              const lastIndex = updated.length - 1;
              // Update the last assistant message incrementally
              updated[lastIndex] = {
                sender: 'assistant',
                text: html.replaceAll('<a', '<a target="_blank" ')
              };
              return updated;
            });
          }

          if (status === 'success') {
            // Done — finalize message and stop polling
            hasMore = false;
            setRequestSortKey((prev) => prev + 1);
            setNextSortKey((prev) => prev + 1);
          }

          await sleep(250);
        }
      } catch (err) {
        console.error('Error polling assistant:', err);
        setMessages((prev) => [
          ...prev,
          { sender: 'assistant', text: 'The assistant did not respond. Please try again later.' }
        ]);
      }
    };

    poll();
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages]);

  // --- Load assessment metadata & initial responses ---
  useEffect(() => {
    async function fetchAssessmentData() {
      try {
        setLoading(true);

        // Fetch assessment metadata (title)
        const assistantRes = await axios.get(
          `${baseApiUri}/ai/completions?type=needs-assessment&fields=query,fileName,title`
        );
        const assistantData = assistantRes.data.completions.find(
          (c) => c.id === assistantId
        );

        console.log('Assistant Data',JSON.stringify(assistantData));

        // Fetch full conversation
        const completionRes = await axios.get(
          `${baseApiUri}/ai/completion/${assistantId}`
        );
        const completionData = completionRes.data;
        console.log('Completion Data', JSON.stringify(completionData));

        // Determine latest message
        const responses = completionData.responses || [];
        const sorted = [...responses].sort((a, b) => a.sortKey - b.sortKey);
        const latest = sorted[sorted.length - 1];

        // --- Determine the highest sortKey safely ---
        let maxSortKey = 0;

        // Case 1: we have responses with numeric sortKeys
        if (sorted.length > 0 && typeof sorted[sorted.length - 1].sortKey === 'number') {
          maxSortKey = sorted[sorted.length - 1].sortKey;
        }

        // Case 2: fallback if assistantData.sortKey is an object (min/max)
        else if (assistantData?.sortKey && typeof assistantData.sortKey.max === 'number') {
          maxSortKey = assistantData.sortKey.max;
        }

        setNextSortKey(maxSortKey + 1);
        console.log(nextSortKey);

        setTitle(assistantData?.title || '');
        setContent(latest?.message || '');
        setChatHistory(sorted);

        // Initialize chat view from history
       // Initialize chat view from history: show user question then assistant reply
const formatted = sorted.flatMap((r) => {
  const items = [];

  // User question (plain text)
  if (r.query) {
    items.push({ sender: 'user', text: r.query });
  }

  // Assistant message (convert markdown to HTML)
  if (r.message) {
    const html = converter.makeHtml(r.message);
    items.push({
      sender: 'assistant',
      text: html.replaceAll('<a', '<a target="_blank" ')
    });
  }

  return items;
});

setMessages(formatted);

      } catch (err) {
        console.error(err);
        setError(err.message || 'Error loading assessment');
      } finally {
        setLoading(false);
      }
    }

    if (assistantId) fetchAssessmentData();
  }, [assistantId, baseApiUri, nextSortKey,converter]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // --- Layout with scroll and pinned input ---
  return (
    <Container maxWidth="xl" sx={{ padding: '2rem', height: '100vh' }}>
      <Box sx={{ display: 'flex', gap: '2rem', height: '100%' }}>
        {/* Left Column: Editable Assessment */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper
            sx={{
              padding: '2rem',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto'
            }}
            elevation={3}
          >
            <Typography variant="h5" gutterBottom>Edit Assessment</Typography>

            <Stack spacing={3} sx={{ flex: 1 }}>
              <TextField
                label="Assessment Title"
                variant="outlined"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />

              <TextField
                label="Assessment Content"
                variant="outlined"
                fullWidth
                multiline
                minRows={12}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                sx={{ flex: 1 }}
              />

              <Box>
                <Button variant="contained" color="primary">
                  Save Changes
                </Button>
              </Box>
            </Stack>
          </Paper>
        </Box>

        {/* Right Column: Chat Window */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Paper
            sx={{
              padding: '2rem',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}
            elevation={3}
          >
            <Typography variant="h5" gutterBottom>AI Assistant</Typography>
            <Divider sx={{ mb: 2 }} />

            <Box
              ref={chatBoxRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #ddd',
                borderRadius: 1,
                p: 2,
                mb: 2,
                backgroundColor: '#fafafa',
                minHeight: 0
              }}
            >
              {messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No chat yet. Type a message below.
                </Typography>
              ) : (
                messages.map((msg, i) => (
                  <Box key={i} sx={{ mb: 1 }}>
                    <Typography
                      variant="subtitle2"
                      color={msg.sender === 'user' ? 'primary.main' : 'secondary.main'}
                    >
                      {msg.sender === 'user' ? 'You:' : 'Assistant:'}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap' }}
                      dangerouslySetInnerHTML={{ __html: msg.text }}
                    />
                    <Divider sx={{ my: 1 }} />
                  </Box>
                ))
              )}
            </Box>

            {/* Input Box */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                variant="outlined"
                placeholder="Type a message..."
                fullWidth
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button variant="contained" onClick={handleSendMessage}>
                Send
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}

